// @flow
import React, {useEffect} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import Cookie from 'js-cookie';
import Experiment from '@homeaway/react-experiment';
import GoogleOneTapLogin from '@homeaway/ha-google-one-tap';
import SiteHeader from '@homeaway/react-site-header';
import TransitionBanner from '@homeaway/react-brand-transition-banner';
import {TripBoardCoachMark} from '@homeaway/react-trip-board-button';
import {useModalRoute} from '@homeaway/react-router-modal';
import LoginExperience from '../LoginExperience/LoginExperience';
import withLingua from '../withLingua/withLingua';
import userActions from '../../actions/user';
import {MODALS} from '../../utils/constants';

import type {LinguaProps} from '../withLingua/withLingua';
import type {InternalToolsType} from '../InternalTools/InternalTools';
import {
    HA_IDA_SERP_GOOGLEONETAP,
    HA_IDA_SERP_HEADER_LOGIN_TEST,
    HA_IDA_V3_LOGIN_TEST,
    HA_GX_PRE_BRAND_TRANSITION,
    HA_GX_POST_BRAND_TRANSITION
} from '../../abacus/experiments';
import ConnectedSkipToContentAnchor from '../ConnectedSkipToContent/ConnectedSkipToContentAnchor';

import type {Node} from 'react';
import type {State as ReduxState} from '../../reducers/index';

const LINGUA_KEYS = {
    preTitle: 'pre.transition.title',
    preInfo: 'pre.transition.info',
    postTitle: 'post.transition.title',
    postInfo: 'post.transition.info',
    postLearnMore: 'post.transition.learnmore',
    learnMoreLink: 'post.transition.learnmore.traveller.link'
};

type StateProps = {
    siteName: string,
    theme: string,
    isMobile: boolean,
    fullLocale: string,
    currency: string,
    brand: string,
    headerData: {},
    isUserLogged: boolean
};

type DispatchProps = {
    setLoggedIn: typeof userActions.setLoggedIn
};

type OwnProps = {
    internalTools?: InternalToolsType,
    showBrandTransitionBanner: string,
    feedbackComponentLingua: {}
};

type Props = OwnProps & StateProps & LinguaProps & DispatchProps;

type ScratchpadCallback = {
    scratchpadLink: Node
};

export const HeaderBar = ({
    siteName,
    theme,
    isMobile,
    internalTools,
    isUserLogged,
    fullLocale,
    currency,
    showBrandTransitionBanner,
    headerData,
    messages,
    feedbackComponentLingua,
    setLoggedIn
}: Props) => {
    const [
        displayNotificationJewel,
        setDisplayNotificationJewel
    ] = React.useState(false);
    const [displayGoogleFrame, setDisplayGoogleFrame] = React.useState(false);
    const [loginModalOpen, setLoginModalOpen] = useModalRoute(MODALS.LOGIN);

    useEffect(() => {
        setLoggedIn({isLoggedIn: isUserLogged});
    }, [isUserLogged, setLoggedIn]);

    const handleShowModal = () => setLoginModalOpen(true);
    const handleCloseModal = () => setLoginModalOpen(false);
    const handleLoginSuccess = (options?: {publicUuid: string}) => {
        if (options && options.publicUuid) {
            window.analyticsdatalayer.publicuuid = options.publicUuid;
        }
        setLoggedIn({isLoggedIn: true});
    };
    const handleTrayChange = (opened: boolean) => {
        if (opened) {
            setDisplayGoogleFrame(true);
        } else {
            setDisplayGoogleFrame(false);
            setDisplayNotificationJewel(false);
        }
    };

    const handleGetTripBoard = ({scratchpadLink}: ScratchpadCallback) => {
        if (!isMobile) {
            return <TripBoardCoachMark scratchpadLink={scratchpadLink} />;
        }
        return null;
    };

    const loginProps = {
        modalMode: true,
        actionLocation: 'search',
        onClose: handleCloseModal
    };

    const siteHeaderProps = {
        dataUrl: '/serp/api/header',
        siteName,
        theme,
        isMobile,
        data: headerData,
        displayTrayJewel: displayNotificationJewel,
        fetchUserData: isUserLogged,
        fullLocale,
        onTrayChange: handleTrayChange,
        internalTools,
        currencyCode: currency,
        tripboard: handleGetTripBoard,
        withoutCountryLanguageSelector: true,
        withoutCurrencySelector: false,
        withoutLocaleSwitcher: false,
        feedbackComponentLingua,
        children: <ConnectedSkipToContentAnchor id="skipToSiteWideNavigation" />
    };

    const preTitle = messages[LINGUA_KEYS.preTitle];
    const preInfo = messages[LINGUA_KEYS.preInfo];
    let postTitle = messages[LINGUA_KEYS.postTitle];
    let postInfo = messages[LINGUA_KEYS.postInfo];
    const learnMore = messages[LINGUA_KEYS.postLearnMore];
    const learnMoreLink = messages[LINGUA_KEYS.learnMoreLink];
    const vgdcCookie = Cookie.get('vgdc');
    const vgdc = showBrandTransitionBanner || vgdcCookie;

    return (
        <div className="HeaderBar" role="group">
            <Experiment name={HA_IDA_SERP_HEADER_LOGIN_TEST}>
                {({On, Off}) => (
                    <React.Fragment>
                        <On>
                            <SiteHeader
                                {...siteHeaderProps}
                                travelerLoginClickHandler={handleShowModal}
                            />
                        </On>
                        <Off>
                            <SiteHeader {...siteHeaderProps} />
                        </Off>
                    </React.Fragment>
                )}
            </Experiment>

            <Experiment name={HA_GX_PRE_BRAND_TRANSITION} variant={1}>
                <TransitionBanner
                    forPreTransition
                    title={preTitle}
                    info={preInfo}
                    useCookieOnClose
                />
            </Experiment>
            <Experiment name={HA_GX_POST_BRAND_TRANSITION} variant={1}>
                <TransitionBanner
                    vgdc={vgdc}
                    forPreTransition={false}
                    title={postTitle}
                    info={postInfo}
                    useCookie={false}
                    learnMore={learnMore}
                    link={learnMoreLink}
                    locale={fullLocale}
                />
            </Experiment>
            {loginModalOpen && (
                <Experiment name={HA_IDA_V3_LOGIN_TEST}>
                    {({On, Off}) => (
                        <>
                            <On>
                                <LoginExperience
                                    useNewEndpoints
                                    {...loginProps}
                                />
                            </On>
                            <Off>
                                <LoginExperience {...loginProps} />
                            </Off>
                        </>
                    )}
                </Experiment>
            )}
            {!isUserLogged && (
                <Experiment name={HA_IDA_SERP_GOOGLEONETAP} variant={1}>
                    <GoogleOneTapLogin
                        displayIFrame={displayGoogleFrame}
                        onCredentialsAvailable={() =>
                            setDisplayNotificationJewel(() => true)
                        }
                        focus
                        closeOnFocusOut
                        loginSuccessCallback={handleLoginSuccess}
                    />
                </Experiment>
            )}
        </div>
    );
};

const mapStateToProps = ({
    system: {siteName, theme, isMobile, fullLocale, currency, brand},
    header,
    user
}: ReduxState): StateProps => ({
    siteName,
    theme,
    isMobile,
    fullLocale,
    currency,
    brand,
    headerData: header,
    isUserLogged:
        (user && user.isLoggedIn) ||
        header.hasOwnProperty('traveler') ||
        header.hasOwnProperty('owner')
});

const mapDispatchToProps = {
    setLoggedIn: userActions.setLoggedIn
};
export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withLingua({
        namespace: 'gx/globalization',
        category: 'rebrandingBrandTransition'
    })
)(HeaderBar);
